import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaterialTable from 'material-table';
import {
    Button,
    Box,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
} from '@mui/material';

import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { axios } from '@/services/axios';
import tableIcons from 'utils/MaterialTableIcons';
import walkthroughIds from '../walkthroughIds';
import {
    ChakraProvider,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
} from '@chakra-ui/react';
import chakratheme from '@/chakratheme';
import { Input } from 'components/v4';
import { Select } from 'chakra-react-select';
import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';

/*
 * Component for colection ports
 * Rendering the Table and the modal form to add another one
 */
export default function EditSipCollectionPort() {
    const muiTheme = useMemo(
        () =>
            createTheme({
                head: {
                    backgroundColor: null,
                },
                overrides: {
                    MuiToolbar: {
                        root: {
                            color: 'rgba(255, 255, 255, 0.7)',
                            backgroundColor: '#212121',
                        },
                    },
                    MuiTable: {
                        root: {
                            WebkitTextFillColor:
                                'rgba(255, 255, 255, 0.7) !important',
                            backgroundColor: '#212121 !important',
                        },
                    },
                    MuiTableHead: {
                        root: {
                            backgroundColor: '#212121 !important',
                        },
                    },
                    MuiTablePagination: {
                        root: {
                            backgroundColor: 'white',
                        },
                    },
                    MuiPaper: {
                        root: {
                            backgroundColor: null,
                        },
                    },
                    MuiIconButton: {
                        label: {
                            color: 'rgba(255, 255, 255, 0.3)',
                        },
                    },
                },
            }),
        [],
    );
    const [port, setPort] = useState(5060);
    const [protocol, setProtocol] = useState('TCP');
    const [errors, setErrors] = useState();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { darkMode } = useSelector((state) => state.settings);

    const chakraSelectDark = chakraSelectDarkMode(darkMode);

    // columns for the table
    const columns = [
        {
            title: 'Port',
            field: 'port',
            width: '50%',
            editComponent: (props) => {
                return (
                    <ChakraProvider theme={chakratheme} resetCSS>
                        <Input
                            className="w-full"
                            name="port"
                            type="number"
                            size={'sm'}
                            value={props.value}
                            onChange={(e) => {
                                props.onChange(e.target.value);
                            }}
                            placeholder="Enter Port"
                        />
                    </ChakraProvider>
                    // <TextField
                    //     type="number"
                    //     variant="outlined"
                    //     size="small"
                    //     value={props.value}
                    //     onChange={(e) => {
                    //         props.onChange(e.target.value);
                    //     }}
                    // />
                );
            },
        },
        {
            title: 'Protocol',
            field: 'protocol',
            width: '50%',
            editComponent: (props) => {
                return (
                    <ChakraProvider theme={chakratheme} resetCSS>
                        <Select
                            className="w-full"
                            value={protocols.find(
                                (a) => a.value == props.value,
                            )}
                            options={protocols}
                            size="sm"
                            onChange={(e) => {
                                props.onChange(e.value);
                            }}
                            placeholder={'- Select Protocol -'}
                            chakraStyles={{
                                ...defaultChakraSelectStyle,
                                ...chakraSelectDark,
                            }}
                        />
                    </ChakraProvider>
                    // <TextField
                    //     select
                    //     variant="standard"
                    //     size="small"
                    //     value={props.value}
                    //     onChange={(e) => props.onChange(e.target.value)}>
                    //     {protocols.map((option) => (
                    //         <MenuItem key={option.label} value={option.value}>
                    //             {option.label}
                    //         </MenuItem>
                    //     ))}
                    // </TextField>
                );
            },
        },
    ];

    const dispatch = useDispatch();
    // state access
    const { state } = useSelector((state) => {
        return {
            ...state.modal,
        };
    });

    const protocols = [
        { value: 'TCP', label: 'TCP' },
        { value: 'UDP', label: 'UDP' },
        { value: 'TLS', label: 'TLS' },
    ];

    // logic for open and close of dialogue
    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setProtocol('TCP');
        setPort(5060);
        setErrors(null);
        setOpen(false);
    };

    /*
     * Function that takes in row data and deletes it
     * The state value of colletion ports is checked and the row passed into the function is removed
     * This new array is dispatched to state
     * The deletedPortIDs array is then deleted in Trunk.jsx
     */
    const deleteHandler = (row) => {
        let result = [];
        state.trunkCollectionPorts.forEach((v) => {
            if (v.port === row.port && v.protocol === row.protocol) {
                dispatch({
                    type: 'CHANGE_MODAL_STATE',
                    payload: {
                        deletedPortIDs: [
                            {
                                id: v.id,
                            },
                            ...state.deletedPortIDs,
                        ],
                    },
                });
            } else {
                const temp = {
                    id: v.id,
                    trunkID: state.id,
                    port: v.port,
                    protocol: v.protocol,
                };
                result.push(temp);
            }
        });
        dispatch({
            type: 'CHANGE_MODAL_STATE',
            payload: {
                trunkCollectionPorts: result,
            },
        });
    };

    /*
     * Submit Handler for API calls
     * Checks the state values have all the required fields for submission to the API
     * A port needs a trunk id which is got from the state
     * It takes the port and protocol from the input fields and adds it to state
     */
    const onSubmitHandler = () => {
        let tempPorts;
        if (state.trunkCollectionPorts) {
            tempPorts = [
                ...state.trunkCollectionPorts,
                {
                    trunkID: state.id,
                    port: port,
                    protocol: protocol,
                },
            ];
        } else {
            tempPorts = [
                {
                    trunkID: state.id,
                    port: port,
                    protocol: protocol,
                },
            ];
        }
        dispatch({
            type: 'CHANGE_MODAL_STATE',
            payload: {
                trunkCollectionPorts: tempPorts,
            },
        });
        setProtocol('TCP');
        handleClose();
    };

    /*
     * If there is a state value of collection ports ->
     * Extracted it and push to state
     */
    useEffect(() => {
        if (state.trunkCollectionPorts && loading === false) {
            let result = [];
            state.trunkCollectionPorts.forEach((v) => {
                const temp = {
                    trunkID: state.id,
                    port: v.port,
                    protocol: v.protocol,
                };
                result.push(temp);
            });
            setRows([...result]);
        }
    }, [state.trunkCollectionPorts, loading]);

    /*
     * When the page first loads, run a API call and set the state/rows based on the return
     * If the mode is edit and collection ports is empty(ie no ports exist)
     * For each value, extract it to the format needed to display it and push to the new result
     * this new result gets dispatched to state
     */
    useEffect(() => {
        setLoading(true);
        if (state.trunkCollectionPorts?.length === 0) {
            axios.get('/trunk/' + state.id).then((res) => {
                let result = [];
                res.data.trunkCollectionPorts.forEach((v) => {
                    if (v.protocol === 0) {
                        v.protocol = 'TCP';
                    } else if (v.protocol === 1) {
                        v.protocol = 'UDP';
                    } else if (v.protocol === 2) {
                        v.protocol = 'TLS';
                    }
                    const temp = {
                        ...v,
                    };
                    result.push(temp);
                });
                dispatch({
                    type: 'CHANGE_MODAL_STATE',
                    payload: {
                        trunkCollectionPorts: result,
                    },
                });
                setRows(result);
                setLoading(false);
            });
        } else {
            let result = [];
            state.trunkCollectionPorts.forEach((v) => {
                if (v.protocol === 0) {
                    v.protocol = 'TCP';
                } else if (v.protocol === 1) {
                    v.protocol = 'UDP';
                } else if (v.protocol === 2) {
                    v.protocol = 'TLS';
                }
                const temp = {
                    ...v,
                };
                result.push(temp);
            });
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: {
                    trunkCollectionPorts: result,
                },
            });
            setRows(result);
            setLoading(false);
        }
    }, []);

    /*
     * Spread operators needed empty arrays or else they would break
     * This just sets some empty state values for the deleted/edited list
     */
    useEffect(() => {
        if (!state.deletedPortIDs) {
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: {
                    deletedPortIDs: [],
                },
            });
        }
        if (!state.editedPortIDs) {
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: {
                    editedPortIDs: [],
                },
            });
        }
    }, []);

    /*
     * Material Table with a dialogue to add a port and editible fields
     * Port and Protocol fields for the dialogue to add a Port
     * This is based on material table documentation
     */
    return (
        <ThemeProvider theme={darkMode ? muiTheme : null}>
            <Box
                sx={{
                    th: {
                        backgroundColor: darkMode
                            ? '#212121 !important'
                            : 'white !important',
                    },
                    td: {
                        color: darkMode ? 'black !important' : null,
                    },
                    boxShadow: darkMode ? 1 : 0,
                    borderWidth: darkMode ? 1 : 0,
                    borderStyle: darkMode ? 'solid' : null,
                    borderColor: darkMode ? '#DDD' : null,
                }}>
                <MaterialTable
                    style={{ height: '100%' }}
                    title="Collection Ports"
                    icons={tableIcons}
                    columns={columns}
                    data={rows}
                    options={{
                        actionsColumnIndex: -1,
                        search: false,
                        sorting: true,
                        pageSize: 3,
                        pageSizeOptions: [],
                        draggable: false,
                    }}
                    actions={[
                        {
                            icon: tableIcons.Delete,
                            tooltip: 'Delete',
                            onClick: (e, rowData) => {
                                deleteHandler(rowData);
                            },
                        },
                        {
                            icon: tableIcons.Add,
                            tooltip: 'Add',
                            isFreeAction: true,
                            onClick: () => {
                                handleOpen();
                            },
                        },
                    ]}
                    // edit logic for inline editing of ports
                    editable={{
                        onRowUpdate: (newData, oldData) =>
                            new Promise((resolve, reject) => {
                                setTimeout(() => {
                                    const dataUpdate = [...rows];
                                    const index = oldData.tableData.id;
                                    dataUpdate[index] = newData;
                                    let result = [];
                                    state.trunkCollectionPorts.forEach(
                                        (port) => {
                                            if (
                                                port.port === oldData.port &&
                                                port.protocol ===
                                                    oldData.protocol
                                            ) {
                                                // if one state value matches the old data - update this value
                                                const temp = {
                                                    ...newData,
                                                    id: port.id,
                                                };
                                                result.push(temp);
                                                dispatch({
                                                    type: 'CHANGE_MODAL_STATE',
                                                    payload: {
                                                        editedPortIDs: [
                                                            ...state.editedPortIDs,
                                                            {
                                                                ...temp,
                                                            },
                                                        ],
                                                    },
                                                });
                                            } else {
                                                // make sure to grab that host and keep it in state
                                                const temp = { ...port };
                                                result.push(temp);
                                            }
                                        },
                                    );
                                    dispatch({
                                        type: 'CHANGE_MODAL_STATE',
                                        payload: {
                                            trunkCollectionPorts: result,
                                        },
                                    });
                                    setRows([...result]);
                                    resolve();
                                }, 1000);
                            }),
                    }}
                    localization={{
                        header: {
                            actions: '',
                        },
                    }}
                />
            </Box>
            <Dialog
                open={open}
                onClose={handleClose}
                sx={{
                    '& .MuiPaper-root': {
                        bgcolor: darkMode ? '#212121' : null,
                        color: darkMode ? 'white' : null,
                    },
                }}>
                <DialogTitle>Add Collection Port</DialogTitle>
                <DialogContent
                    sx={{
                        height: '25vh',
                        width: '40vh',
                    }}>
                    <ChakraProvider theme={chakratheme} resetCSS>
                        <FormControl mb={3}>
                            <FormLabel color={darkMode ? 'white' : null}>
                                Protocol
                            </FormLabel>
                            <Box
                                sx={{
                                    // marginBottom: '10px',
                                    display: 'flex',
                                    width: 'full',
                                    alignItems: 'flex-start',
                                }}
                                data-walkthroughid={
                                    walkthroughIds.endpoints.modal
                                        .ufProtocolSelect
                                }>
                                <Select
                                    className="w-full"
                                    value={protocols.find(
                                        (a) => a.value == protocol,
                                    )}
                                    options={protocols}
                                    size="sm"
                                    onChange={(e) => {
                                        setProtocol(e.value);
                                    }}
                                    placeholder={'- Select Protocol -'}
                                    chakraStyles={{
                                        ...defaultChakraSelectStyle,
                                        ...chakraSelectDark,
                                    }}
                                />
                            </Box>
                        </FormControl>

                        <FormControl isInvalid={errors?.port} isRequired>
                            <FormLabel color={darkMode ? 'white' : null}>
                                Port
                            </FormLabel>
                            <Box
                                sx={{
                                    // marginBottom: '10px',
                                    display: 'flex',
                                    width: 'full',
                                    alignItems: 'flex-start',
                                }}
                                data-walkthroughid={
                                    walkthroughIds.endpoints.modal.ufPortInput
                                }>
                                <Input
                                    className="w-full"
                                    type="number"
                                    size={'sm'}
                                    textColor={darkMode ? 'white' : null}
                                    value={port}
                                    placeholder="Enter Port"
                                    sx={{ width: '100%' }}
                                    onChange={(e) => {
                                        setErrors({ port: '' });
                                        setPort(parseInt(e.target.value));
                                        let reg = new RegExp(/^[1-9]\d*$/).test(
                                            e.target.value,
                                        );
                                        if (!reg) {
                                            setErrors({
                                                port: 'Only positive integers are allowed',
                                            });
                                        } else {
                                            setErrors(null);
                                        }
                                    }}
                                />
                            </Box>
                            {errors?.port ? (
                                <FormErrorMessage>
                                    {errors.port}
                                </FormErrorMessage>
                            ) : (
                                <FormHelperText>
                                    Please enter a collection port
                                </FormHelperText>
                            )}
                        </FormControl>
                    </ChakraProvider>
                    {/* <TextField
                        select
                        sx={{ width: '100%', marginBottom: 2 }}
                        label="Protocol"
                        id="collection-port-protocol"
                        value={protocol}
                        onChange={(e) => {
                            setProtocol(e.target.value);
                        }}
                        data-walkthroughid={
                            walkthroughIds.endpoints.modal.ufProtocolSelect
                        }>
                        {protocols.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField> */}
                    {/* <TextField
                        sx={{ width: '100%' }}
                        type="number"
                        error={errors?.port}
                        value={port}
                        helperText={errors?.port}
                        label="Port"
                        onChange={(e) => {
                            setErrors({ port: '' });
                            setPort(parseInt(e.target.value));
                            let reg = new RegExp(/^[1-9]\d*$/).test(
                                e.target.value,
                            );
                            if (!reg) {
                                setErrors({
                                    port: 'Only positive integers are allowed',
                                });
                            } else {
                                setErrors(null);
                            }
                        }}
                        data-walkthroughid={
                            walkthroughIds.endpoints.modal.ufPortInput
                        }
                    /> */}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button
                        sx={{ backgroundcolor: '#0d6efd', color: '#ffffff' }}
                        variant="contained"
                        disabled={errors || !port}
                        style={{
                            color: darkMode ? 'white' : null,
                            backgroundColor: darkMode ? '#1976d2' : null,
                            opacity:
                                darkMode && (errors || !port) ? '0.4' : null,
                        }}
                        onClick={() => {
                            onSubmitHandler();
                        }}
                        data-walkthroughid={
                            walkthroughIds.endpoints.modal.ufAddButton
                        }>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}
